import {
  containsPrivacySensitiveData,
  getVisibleNotitieText,
  validateAnnotatedNotitie,
} from '@/apps/agenda/components/detailPanel/annotatedNotitie';
import { AgendaApiResponse } from '@/apps/agenda/types';
import { EenheidInregelingStatusResponse } from '@/hooks/useGetEenhedenInregelingStatus';

const getAfspraken = (agenda: AgendaApiResponse) => [
  ...agenda.langdurig.map((row) => row.afspraak),
  ...agenda.week.weeks.flatMap((week) => week.days.flatMap((day) => day.rows.map((row) => row.afspraak))),
];

export type AgendaInregelingDetails = {
  aanvraagnotitie?: string;
  uitlooplocatie?: string;
  privacyBlocked?: true;
};

export const findAgendaInregelingDetails = (
  eenheidNummer: string,
  agenda?: AgendaApiResponse,
  eenhedenStatus?: readonly EenheidInregelingStatusResponse[],
): AgendaInregelingDetails | undefined => {
  if (!eenhedenStatus) return undefined;

  const inregeling = eenhedenStatus.find(({ nummer }) => nummer === eenheidNummer)?.inregeling;
  // The shared status hook stays lenient for Agenda, so MWD validates the fields it uses.
  if (!inregeling || typeof inregeling !== 'object') return undefined;
  const { afspraakId, uitlooplocatie } = inregeling;
  if (typeof afspraakId !== 'string' || !afspraakId) return undefined;

  // The location comes from the status response; the Agenda note is an optional enrichment on top of it.
  const details: AgendaInregelingDetails = {
    uitlooplocatie: (typeof uitlooplocatie === 'string' && uitlooplocatie.trim()) || undefined,
  };
  if (!agenda) return details;

  const aanvraagnotitie = getAfspraken(agenda).find(({ id }) => id === afspraakId)?.aanvraagnotitie;
  if (!aanvraagnotitie) return details;

  if (
    typeof aanvraagnotitie.text !== 'string' ||
    !Array.isArray(aanvraagnotitie.marks) ||
    aanvraagnotitie.marks.some(
      (mark) => !mark || typeof mark !== 'object' || !Number.isInteger(mark.start) || !Number.isInteger(mark.end),
    ) ||
    validateAnnotatedNotitie(aanvraagnotitie).length > 0
  ) {
    return { ...details, privacyBlocked: true };
  }

  // Names rely on Agenda's PRIVATE markings; contact checks provide an additional safeguard.
  if (containsPrivacySensitiveData(aanvraagnotitie)) {
    return { ...details, privacyBlocked: true };
  }
  const text = getVisibleNotitieText(aanvraagnotitie, false).trim();
  if (!text) return details;
  if (containsPrivacySensitiveData({ text, marks: [] })) {
    return { ...details, privacyBlocked: true };
  }

  return { ...details, aanvraagnotitie: text };
};
